import { z } from 'zod';
import { apiFetch } from '../api-fetch.js';

const status = z
  .string()
  .transform((value) =>
    [
      'available',
      'unavailable',
      'unverified',
      'not_selected',
      'requires_approval',
    ].includes(value)
      ? value
      : 'unverified',
  );
const row = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  status,
});
export const ToolInventorySchema = z.object({
  version: z.literal(1),
  status: z
    .string()
    .transform((value) =>
      ['complete', 'partial', 'error', 'scope_unavailable'].includes(value)
        ? value
        : 'error',
    ),
  checkedAt: z.string().datetime(),
  groups: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      kind: z.enum(['builtin', 'connection']),
      status,
      reason: z.string().nullable(),
      tools: z.array(row),
    }),
  ),
});
export type ToolInventory = z.infer<typeof ToolInventorySchema>;
export class ToolInventoryAccessError extends Error {
  constructor() {
    super('This chat or workspace is no longer available.');
  }
}
export async function fetchToolInventory(
  scope: {
    conversationId?: string | null;
    workspaceId?: string | null;
    hasFiles?: boolean;
  },
  signal: AbortSignal,
): Promise<ToolInventory> {
  if (!scope.conversationId && !scope.workspaceId)
    throw new Error('Choose a workspace to inspect its tools.');
  const query = new URLSearchParams(
    scope.conversationId
      ? { conversation_id: scope.conversationId }
      : scope.workspaceId
        ? { workspace_id: scope.workspaceId }
        : {},
  );
  if (scope.hasFiles) query.set('has_files', 'true');
  signal.throwIfAborted();
  const response = await apiFetch(`/v1/conversations/tool-inventory?${query}`, {
    signal,
    cache: 'no-store',
  });
  if (response.status === 404) throw new ToolInventoryAccessError();
  if (!response.ok) throw new Error('Tools could not be checked. Try again.');
  return ToolInventorySchema.parse(await response.json());
}
